import {
  type ElementSelection,
  type HexEditPreviewMessage,
  HexVersionId,
} from "@hex/common";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { isEqual } from "lodash";

import { DataAppChromeColors } from "../../util/dataAppChromeTheme.js";
import { RootState } from "../store";

const MAX_RUNTIME_ERRORS = 20;

export type DataAppViewMode = "preview" | "code" | "select" | "edit";

export interface DataAppRuntimeError {
  message: string;
  stack?: string;
  timestamp: number;
}

/**
 * Chrome colors currently being applied to host UI (AppNavbar) next to the
 * data app. Populated from the runtime-sampled value postMessaged by the
 * iframe after render.
 */
export type DataAppThemeState = DataAppChromeColors;

/** Where to put the cursor in the source panel, in offsets into the file. */
export interface DataAppSourceReveal {
  path: string;
  start: number;
  end: number;
  nonce: number;
}

export interface SelectModeEntry {
  seq: number;
  fromButton: boolean;
}

export interface DataAppVersionState {
  files: Record<string, string> | null;
  entryPoint: string | null;
  lastModifiedAt: number | null;
  isLoading: boolean;
  runtimeErrors: DataAppRuntimeError[];
  /** Which file is open in the source panel of the App Builder tab. */
  selectedFile: string | null;
  /**
   * A one-shot request to put the cursor somewhere in the open file. The
   * editor consumes it once and clears it, so the same span can be asked for
   * again -- `nonce` is what makes a second click of the same element move a
   * cursor the user has since moved away.
   */
  sourceReveal: DataAppSourceReveal | null;
  /** Whether the App Builder tab shows the live preview or the source panel. */
  viewMode: DataAppViewMode;
  /**
   * The most recent switch from preview into select or edit mode: whether the
   * top-bar Select button made it, and a running count so one entry can be
   * told from the next. Null until the first entry; kept after leaving so the
   * count keeps climbing.
   */
  lastSelectModeEntry: SelectModeEntry | null;
  appTheme: DataAppThemeState | null;
  /** The chart element selected in edit mode (null = nothing selected). */
  elementSelection: ElementSelection | null;
  /**
   * Count of `setElementSelection` dispatches stamped `isUserInteraction`
   * (see that payload's doc). Consumers treat each bump as a one-shot
   * scroll/focus request. Clears never bump it, so "revision unchanged" does
   * NOT mean the selection still exists — check that separately.
   */
  selectionRevision: number;
  /**
   * What is under the pointer in select/edit mode: a seeded component type
   * ("LineChart") or a text element's kind ("Heading"). Null when nothing
   * selectable is hovered.
   */
  elementHoverComponent: string | null;
  /**
   * Frame-reported qualifier for it, in display form: a chart's region
   * ("Legend") or a preview of a text element's words.
   */
  elementHoverRegion: string | null;
  /** Ephemeral edit-panel preview bound for the iframe (never persisted). */
  editPreview: EditPreviewState | null;
}

/**
 * `values: null` means "clear the preview for `target`" — the shape mirrors
 * the HEX_EDIT_PREVIEW message, and a clear must still name its target.
 * `lastModifiedAt` is the files era the preview was computed against; the
 * reducer drops writes from a different era (see setEditPreview).
 */
export interface EditPreviewState {
  target: HexEditPreviewMessage["target"];
  values: Record<string, unknown> | null;
  lastModifiedAt: number | null;
}

type DataAppSliceState = Record<HexVersionId, DataAppVersionState | undefined>;

// `isLoading` defaults to `true` so that a freshly-synthesized entry — created
// e.g. by `setDataAppViewMode` / `setDataAppSelectedFile` before the actual
// load has fired — is treated as "not loaded yet" by `selectIsLoaded`. The
// flag is flipped to `false` only once we hear back from the server via
// `setDataAppState` or `setDataAppNotFound`.
const initialVersionState = (): DataAppVersionState => ({
  files: null,
  entryPoint: null,
  lastModifiedAt: null,
  isLoading: true,
  runtimeErrors: [],
  selectedFile: null,
  sourceReveal: null,
  viewMode: "preview",
  lastSelectModeEntry: null,
  appTheme: null,
  elementSelection: null,
  selectionRevision: 0,
  elementHoverComponent: null,
  elementHoverRegion: null,
  editPreview: null,
});

const dataAppSlice = createSlice({
  name: "dataApp",
  initialState: {} as DataAppSliceState,
  reducers: {
    setDataAppLoading: (
      state,
      action: PayloadAction<{ hexVersionId: HexVersionId }>,
    ) => {
      const { hexVersionId } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      state[hexVersionId] = { ...existing, isLoading: true };
    },
    setDataAppState: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        files: Record<string, string>;
        entryPoint: string;
        lastModifiedAt: number;
      }>,
    ) => {
      const { entryPoint, files, hexVersionId, lastModifiedAt } =
        action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      state[hexVersionId] = {
        ...existing,
        files,
        entryPoint,
        lastModifiedAt,
        isLoading: false,
        runtimeErrors: [],
        // A preview is meaningless against files it wasn't made for. Clearing
        // here (not in an effect) is what keeps the post-republish re-embed
        // from replaying a stale preview into the fresh iframe. The
        // selection deliberately survives (the panel must outlive Apply →
        // republish), but its embedded currentValues are a snapshot from
        // before the file change — panel consumers must re-derive current
        // values rather than trust them after this.
        editPreview: null,
      };
    },
    setDataAppTheme: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        theme: DataAppThemeState | null;
      }>,
    ) => {
      const { hexVersionId, theme } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      state[hexVersionId] = { ...existing, appTheme: theme };
    },
    setDataAppNotFound: (
      state,
      action: PayloadAction<{ hexVersionId: HexVersionId }>,
    ) => {
      const { hexVersionId } = action.payload;
      state[hexVersionId] = { ...initialVersionState(), isLoading: false };
    },
    addRuntimeError: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        error: DataAppRuntimeError;
      }>,
    ) => {
      const { error, hexVersionId } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      const errors = [...existing.runtimeErrors, error].slice(
        -MAX_RUNTIME_ERRORS,
      );
      state[hexVersionId] = { ...existing, runtimeErrors: errors };
    },
    clearRuntimeErrors: (
      state,
      action: PayloadAction<{ hexVersionId: HexVersionId }>,
    ) => {
      const { hexVersionId } = action.payload;
      const existing = state[hexVersionId];
      if (existing != null) {
        state[hexVersionId] = { ...existing, runtimeErrors: [] };
      }
    },
    setDataAppViewMode: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        viewMode: DataAppViewMode;
        /** True when the top-bar Select button itself made the change. */
        fromButton?: boolean;
      }>,
    ) => {
      const { fromButton = false, hexVersionId, viewMode } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      const inSelectionMode = viewMode === "select" || viewMode === "edit";
      const wasInSelectionMode =
        existing.viewMode === "select" || existing.viewMode === "edit";
      const entering = inSelectionMode && !wasInSelectionMode;
      state[hexVersionId] = {
        ...existing,
        viewMode,
        lastSelectModeEntry: entering
          ? { seq: (existing.lastSelectModeEntry?.seq ?? 0) + 1, fromButton }
          : existing.lastSelectModeEntry,
        elementSelection: inSelectionMode ? existing.elementSelection : null,
        elementHoverComponent: inSelectionMode
          ? existing.elementHoverComponent
          : null,
        elementHoverRegion: inSelectionMode
          ? existing.elementHoverRegion
          : null,
      };
    },
    /**
     * Backs the selection flow out one level: a selection (locked-in or
     * provisional) → select mode with nothing selected, empty select mode →
     * preview. Shared by the edit panel Close button, host Escape, and
     * Escape relayed from the iframe.
     */
    dismissElementSelection: (
      state,
      action: PayloadAction<{ hexVersionId: HexVersionId }>,
    ) => {
      const { hexVersionId } = action.payload;
      const existing = state[hexVersionId];
      if (existing == null) return;
      if (
        (existing.viewMode === "edit" || existing.viewMode === "select") &&
        existing.elementSelection != null
      ) {
        state[hexVersionId] = {
          ...existing,
          viewMode: "select",
          elementSelection: null,
        };
        return;
      }
      if (existing.viewMode === "select" || existing.viewMode === "edit") {
        state[hexVersionId] = {
          ...existing,
          viewMode: "preview",
          elementSelection: null,
          elementHoverComponent: null,
          elementHoverRegion: null,
        };
      }
    },
    setElementSelection: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        selection: ElementSelection | null;
        /**
         * Set only where the dispatch is a genuine fresh user interaction
         * (element click, chart-menu edit request) — never on the maintenance
         * dispatches (apply fold, re-embed reconcile, clears). Bumps
         * `selectionRevision`.
         */
        isUserInteraction?: true;
      }>,
    ) => {
      const { hexVersionId, isUserInteraction, selection } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      const stamped = isUserInteraction === true && selection != null;
      const nextRevision = stamped
        ? existing.selectionRevision + 1
        : existing.selectionRevision;
      // Content-identical means identity, geometry, and reported values all
      // match. mountId, currentValues, and codeDefinedKeys must participate:
      // Apply folds edits into currentValues only, the post-republish
      // reconcile changes mountId only, and provenance may change
      // independently of both.
      const previous = existing.elementSelection;
      const unchanged =
        selection != null &&
        previous != null &&
        previous.kind === selection.kind &&
        previous.rect.x === selection.rect.x &&
        previous.rect.y === selection.rect.y &&
        previous.rect.width === selection.rect.width &&
        previous.rect.height === selection.rect.height &&
        (selection.kind === "text"
          ? previous.kind === "text" &&
            previous.tagId === selection.tagId &&
            previous.elementType === selection.elementType &&
            previous.label === selection.label
          : previous.kind === "chart" &&
            previous.component === selection.component &&
            previous.mountId === selection.mountId &&
            previous.instanceId === selection.instanceId &&
            previous.region === selection.region &&
            previous.seriesKey === selection.seriesKey &&
            isEqual(previous.currentValues, selection.currentValues) &&
            isEqual(previous.codeDefinedKeys, selection.codeDefinedKeys));
      // An unstamped content-identical dispatch is a no-op, so an app looping
      // selection posts can't churn every subscriber with fresh objects. A
      // stamped one can legitimately re-select the already-selected element
      // (e.g. reopening "Edit style" on the same chart): keep the object
      // reference but still advance the revision so the panel scrolls/focuses
      // again.
      if (unchanged && !stamped) return;
      state[hexVersionId] = {
        ...existing,
        elementSelection: unchanged ? previous : selection,
        selectionRevision: nextRevision,
      };
    },
    /**
     * Refreshes the selected chart's reported values in place, when the frame
     * re-reports them because the chart's props moved under the live
     * selection. Deliberately not `setElementSelection`: the selection has not
     * changed, and re-setting it would read downstream as a fresh click.
     */
    setElementSelectionValues: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        currentValues: Record<string, unknown>;
      }>,
    ) => {
      const { currentValues, hexVersionId } = action.payload;
      const existing = state[hexVersionId];
      const selection = existing?.elementSelection;
      if (existing == null || selection == null || selection.kind !== "chart") {
        return;
      }
      // Charts re-report on every prop change, most of which don't touch a
      // reported value; an unchanged object must not re-render the panel.
      if (
        JSON.stringify(selection.currentValues) ===
        JSON.stringify(currentValues)
      ) {
        return;
      }
      state[hexVersionId] = {
        ...existing,
        elementSelection: { ...selection, currentValues },
      };
    },
    setElementHoverComponent: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        component: string | null;
        region?: string | null;
      }>,
    ) => {
      const { component, hexVersionId, region = null } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      if (
        existing.elementHoverComponent === component &&
        existing.elementHoverRegion === region
      ) {
        return;
      }
      state[hexVersionId] = {
        ...existing,
        elementHoverComponent: component,
        elementHoverRegion: region,
      };
    },
    setEditPreview: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        preview: EditPreviewState | null;
      }>,
    ) => {
      const { hexVersionId, preview } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      // A panel debounce timer can fire in the gap between setDataAppState
      // (which cleared editPreview for the new files) and the panel's own
      // invalidation effect. Dropping writes stamped with a different files
      // era keeps that late write from replaying into the fresh iframe.
      if (
        preview != null &&
        preview.lastModifiedAt !== existing.lastModifiedAt
      ) {
        return;
      }
      state[hexVersionId] = { ...existing, editPreview: preview };
    },
    setDataAppSelectedFile: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        selectedFile: string | null;
      }>,
    ) => {
      const { hexVersionId, selectedFile } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      state[hexVersionId] = { ...existing, selectedFile };
    },
    requestDataAppSourceReveal: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        path: string;
        start: number;
        end: number;
      }>,
    ) => {
      const { end, hexVersionId, path, start } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      state[hexVersionId] = {
        ...existing,
        sourceReveal: {
          path,
          start,
          end,
          nonce: (existing.sourceReveal?.nonce ?? 0) + 1,
        },
      };
    },
    clearDataAppSourceReveal: (
      state,
      action: PayloadAction<{ hexVersionId: HexVersionId; nonce: number }>,
    ) => {
      const { hexVersionId, nonce } = action.payload;
      const existing = state[hexVersionId];
      if (existing?.sourceReveal?.nonce !== nonce) {
        return;
      }
      state[hexVersionId] = { ...existing, sourceReveal: null };
    },
  },
});

export const {
  addRuntimeError,
  clearDataAppSourceReveal,
  clearRuntimeErrors,
  dismissElementSelection,
  requestDataAppSourceReveal,
  setDataAppLoading,
  setDataAppNotFound,
  setDataAppSelectedFile,
  setDataAppState,
  setDataAppTheme,
  setDataAppViewMode,
  setEditPreview,
  setElementHoverComponent,
  setElementSelection,
  setElementSelectionValues,
} = dataAppSlice.actions;

export const dataAppReducer = dataAppSlice.reducer;

const selectVersionState = (
  state: RootState,
  hexVersionId: HexVersionId,
): DataAppVersionState | undefined => state.dataApp[hexVersionId];

export const dataAppSelectors = {
  selectFiles: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.files ?? null,

  /**
   * Returns whether a specific path exists in the loaded VFS. `null` means
   * the VFS hasn't been loaded yet (callers should typically treat this as
   * "assume present" so we don't flag rows as missing before we know).
   *
   * Designed for `useSelector` subscriptions in per-row components: it
   * returns a scalar `boolean | null`, so the default referential-equality
   * check is sufficient and rows only re-render when their own path's
   * existence flips. Prefer this over reading `selectFiles` and computing
   * existence at render time, which causes a re-render on every unrelated
   * VFS write.
   */
  selectFileExists: (
    state: RootState,
    hexVersionId: HexVersionId,
    path: string,
  ): boolean | null => {
    const files = selectVersionState(state, hexVersionId)?.files;
    if (files == null) {
      return null;
    }
    return Object.hasOwn(files, path);
  },

  // True once we've heard back from the server (either `setDataAppState` or
  // `setDataAppNotFound`). Before that, `selectFiles` returns `null` both for
  // the pre-load case and the genuinely-empty case, and callers that need to
  // distinguish "not loaded yet" from "confirmed no files" should use this.
  selectIsLoaded: (state: RootState, hexVersionId: HexVersionId) => {
    const versionState = selectVersionState(state, hexVersionId);
    return versionState != null && !versionState.isLoading;
  },

  selectEntryPoint: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.entryPoint ?? null,

  selectIsLoading: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.isLoading ?? false,

  selectRuntimeErrors: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.runtimeErrors ?? [],

  selectHasApp: (state: RootState, hexVersionId: HexVersionId) =>
    (selectVersionState(state, hexVersionId)?.files ?? null) != null,

  selectLastModifiedAt: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.lastModifiedAt ?? null,

  selectAppTheme: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.appTheme ?? null,

  selectSelectedFile: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.selectedFile ?? null,

  selectSourceReveal: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.sourceReveal ?? null,

  selectViewMode: (
    state: RootState,
    hexVersionId: HexVersionId,
  ): DataAppViewMode =>
    selectVersionState(state, hexVersionId)?.viewMode ?? "preview",

  selectElementSelection: (
    state: RootState,
    hexVersionId: HexVersionId,
  ): ElementSelection | null =>
    selectVersionState(state, hexVersionId)?.elementSelection ?? null,

  selectLastSelectModeEntry: (
    state: RootState,
    hexVersionId: HexVersionId,
  ): SelectModeEntry | null =>
    selectVersionState(state, hexVersionId)?.lastSelectModeEntry ?? null,

  selectSelectionRevision: (
    state: RootState,
    hexVersionId: HexVersionId,
  ): number => selectVersionState(state, hexVersionId)?.selectionRevision ?? 0,

  selectElementHoverComponent: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.elementHoverComponent ?? null,

  selectElementHoverRegion: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.elementHoverRegion ?? null,

  selectEditPreview: (
    state: RootState,
    hexVersionId: HexVersionId,
  ): EditPreviewState | null =>
    selectVersionState(state, hexVersionId)?.editPreview ?? null,
};
